// @ts-nocheck
import PropTypes from "prop-types";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import { Autocomplete, InputAdornment, TextField } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash";
import { useDispatch } from "react-redux";
import { fetchAllCompaniesAction } from "../../store/actions/client";
import { alertDialogAciton } from "../../store/actions/auth";
import { Search } from "@mui/icons-material";

const CompaniesAutocomplete = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  readOnly,
  disabled,
  fetchOptions,
  getOptionLabel,
  getOptionValue,
  noOptionsText,
  renderOptionContent,
  borderColor,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const dispatch = useDispatch();

  // A seleção é OPCIONAL de controlar: AddClientModal e AssignClientModal passam
  // `value` comentado e cuidam dos campos via setFieldValue. Antes, o defaultProp
  // `value: null` fazia o componente se achar controlado em null; como
  // `getOptionLabel` é uma arrow inline (nova referência a cada render do pai) e
  // estava na dep list do effect abaixo, TODO re-render do pai caía no ramo
  // `!value` e limpava o input — ou seja, escolher a empresa preenchia os campos
  // e apagava o nome do campo de busca, como se nada tivesse sido selecionado.
  const [internalValue, setInternalValue] = useState(null);
  const isControlled = value !== undefined && value !== null;
  const selectedValue = isControlled ? value : internalValue;

  // Ref para o label não entrar na dep list do effect (identidade instável).
  const getOptionLabelRef = useRef(getOptionLabel);
  getOptionLabelRef.current = getOptionLabel;

  // `fetchOptions` historically gated whether this component fetches at all.
  // Callers pass it either as a boolean (just "enable fetching") or as a
  // custom fetcher function. Normalize: enabled when truthy; only call it as a
  // function when it actually is one (avoids "fetchOptions of type boolean
  // ... expected function" PropTypes noise and a TypeError if invoked).
  const fetchData = useCallback(async () => {
    if (fetchOptions) {
      setLoading(true);
      dispatch(
        fetchAllCompaniesAction({
          payload: {
            params: {
              search: inputValue,
            },
          },
          successCallback: (data) => {
            // Guarda: o contrato de /getCompanies é doc de `businessprofiles` (objeto com
            // companyName). Quando a API regrediu pra array de STRINGS, `getOptionLabel`
            // devolvia undefined e o MUI renderizava N linhas em BRANCO — sem erro e sem
            // "No Companies Found". Descartar o que não é objeto faz a falha aparecer.
            const list = Array.isArray(data?.data) ? data.data : [];
            setOptions(list.filter((o) => o && typeof o === "object"));
            setLoading(false);
          },
          failureCallback: (msg) => {
            dispatch(
              alertDialogAciton({
                open: true,
                title: "Error",
                message: msg || "Failed to fetch companies",
              }),
            );
            setLoading(false);
          },
        }),
      );
    }
  }, [fetchOptions, dispatch, inputValue]);

  // Debounced fetch function using useMemo
  const debouncedFetch = useMemo(() => debounce(fetchData, 300), [fetchData]);

  // Fetch initial options on mount
  useEffect(() => {
    debouncedFetch("");
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch, inputValue]);

  // Só reage a mudança REAL de seleção — não a re-render do pai.
  useEffect(() => {
    const label = getOptionLabelRef.current;
    if (selectedValue && label) {
      setInputValue(label(selectedValue) || "");
    } else if (!selectedValue) {
      setInputValue("");
    }
  }, [selectedValue]);

  // Handle input change with debounce
  // const handleInputChange = (_, newVal, reason) => {
  //   if (reason === "input") {
  //     setInputValue(newVal);
  //     debouncedFetch(newVal);
  //   }
  // };
  const handleInputChange = (_, newVal, reason) => {
    if (reason === "input") {
      setInputValue(newVal);
      debouncedFetch(newVal);
    } else if (reason === "clear") {
      setInputValue("");
      debouncedFetch("");
    }
  };

  // Handle selection
  // const handleChange = (_, newVal) => {
  //   if (onChange && newVal) {
  //     const valueToSet = getOptionValue ? getOptionValue(newVal) : newVal;
  //     onChange(name, valueToSet);
  //   }
  // };
  const handleChange = (_, newVal) => {
    if (!isControlled) setInternalValue(newVal ?? null);

    if (onChange) {
      const valueToSet = getOptionValue ? getOptionValue(newVal) : newVal;
      onChange(name, valueToSet);
    }

    // Update inputValue when selection is made
    if (newVal && getOptionLabel) {
      setInputValue(getOptionLabel(newVal) || "");
    } else if (!newVal) {
      setInputValue("");
    }
  };

  return (
    <Autocomplete
      fullWidth
      readOnly={readOnly}
      disabled={disabled}
      options={options}
      loading={loading}
      getOptionLabel={getOptionLabel || ((option) => option?.toString() || "")}
      value={selectedValue ?? null}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      // componentsProps={{
      //   paper: {
      //     style: {
      //       display: "block",
      //       background: "black",
      //       outline: "1px solid white",
      //       borderRadius: "8px",
      //     },
      //   },
      // }}
      componentsProps={{
        paper: {
          sx: {
            display: "block",
            background: "black",
            outline: "1px solid white",
            borderRadius: "8px",
            "& .MuiAutocomplete-noOptions": {
              color: "white",
            },
            "& .MuiAutocomplete-loading": {
              color: "white",
            },
          },
        },
      }}
      noOptionsText={noOptionsText || "No Options Available"}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          placeholder={placeholder}
          onBlur={onBlur}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <Search sx={{ color: "white" }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            style: { color: "white" },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              paddingY: "7px !important",
              "& fieldset": {
                borderRadius: "8px",
                borderColor: borderColor
                  ? borderColor
                  : getCssVariableHandler({
                      variableName: "--primary-pink-hex",
                    }),
              },
              "&:hover fieldset": {
                borderColor: borderColor
                  ? borderColor
                  : getCssVariableHandler({
                      variableName: "--primary-pink-hex",
                    }),
              },
              "&.Mui-focused fieldset": {
                borderColor: borderColor
                  ? borderColor
                  : getCssVariableHandler({
                      variableName: "--primary-pink-hex",
                    }),
              },
              color: "white",
            },
            "& .MuiInputBase-input": {
              color: "white",
            },
            "& .MuiAutocomplete-popupIndicator": {
              color: "white",
            },
          }}
        />
      )}
      renderOption={
        renderOptionContent
          ? (props, option) => <li {...props}>{renderOptionContent(option)}</li>
          : undefined
      }
    />
  );
};

CompaniesAutocomplete.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  // Accepts a boolean ("enable internal fetch") or a custom fetcher function.
  fetchOptions: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  noOptionsText: PropTypes.string,
  renderOptionContent: PropTypes.func,
  borderColor: PropTypes.string,
};

CompaniesAutocomplete.defaultProps = {
  placeholder: "Type Here",
  readOnly: false,
  disabled: false,
  noOptionsText: "No Options Available",
  value: null,
  onBlur: () => {},
  fetchOptions: false,
};

export default CompaniesAutocomplete;